<template>
  <div class="pg-trans-rec">
    <div class="pg-trans-rec__header">
      <h6 class="pg-trans-rec__title">
        <i class="material-icons-round">mic</i>
        Translation Recording Game
      </h6>
    </div>

    <!-- Translation Exercises -->
    <div class="pg-trans-rec__section">
      <label class="pg-trans-rec__section-label">
        <i class="material-icons-round">task_alt</i>
        Translation Exercises
      </label>
      <div class="pg-trans-rec__list">
        <div
          v-for="(exercise, index) in sectionData.content.exercises"
          :key="index"
          class="pg-trans-rec__item"
        >
          <div class="pg-trans-rec__item-head">
            <span class="pg-trans-rec__index">Exercise {{ index + 1 }}</span>
            <div class="pg-trans-rec__item-actions">
              <button
                type="button"
                class="pg-trans-rec__icon-btn pg-trans-rec__icon-btn--danger"
                title="Delete exercise"
                @click="removeExercise(index)"
              >
                <i class="material-icons-round">delete</i>
              </button>
            </div>
          </div>

          <div class="pg-trans-rec__item-body">
            <!-- Context Section -->
            <div class="p-field">
              <label class="p-field__label">
                <i class="material-icons-round pg-trans-rec__field-ico">info</i>
                Context/Background
              </label>
              <textarea
                v-model="exercise.context"
                class="p-input p-input--area"
                rows="2"
                placeholder="번역 배경 정보나 맥락을 입력하세요..."
              />
            </div>

            <!-- Language Settings -->
            <div class="pg-trans-rec__subsection">
              <label class="pg-trans-rec__sub-label">
                <i class="material-icons-round pg-trans-rec__field-ico">language</i>
                Language Settings
              </label>
              <div class="p-form-grid p-form-grid--2">
                <div class="p-field">
                  <label class="p-field__label">Source Language</label>
                  <select v-model="exercise.sourceLanguage" class="p-input">
                    <option value="">언어 선택</option>
                    <option value="ko">🇰🇷 한국어 (Korean)</option>
                    <option value="en">🇺🇸 English</option>
                    <option value="ja">🇯🇵 日本語 (Japanese)</option>
                    <option value="zh">🇨🇳 中文 (Chinese)</option>
                    <option value="es">🇪🇸 Español (Spanish)</option>
                    <option value="fr">🇫🇷 Français (French)</option>
                    <option value="vi">🇻🇳 Tiếng Việt (Vietnamese)</option>
                    <option value="mn">🇲🇳 Монгол хэл (Mongolian)</option>
                    <option value="ru">🇷🇺 Русский (Russian)</option>
                  </select>
                </div>
                <div class="p-field">
                  <label class="p-field__label">Target Language</label>
                  <select v-model="exercise.targetLanguage" class="p-input">
                    <option value="">언어 선택</option>
                    <option value="ko">🇰🇷 한국어 (Korean)</option>
                    <option value="en">🇺🇸 English</option>
                    <option value="ja">🇯🇵 日本語 (Japanese)</option>
                    <option value="zh">🇨🇳 中文 (Chinese)</option>
                    <option value="es">🇪🇸 Español (Spanish)</option>
                    <option value="fr">🇫🇷 Français (French)</option>
                    <option value="vi">🇻🇳 Tiếng Việt (Vietnamese)</option>
                    <option value="mn">🇲🇳 Монгол хэл (Mongolian)</option>
                    <option value="ru">🇷🇺 Русский (Russian)</option>
                  </select>
                </div>
              </div>
            </div>

            <!-- Text Content -->
            <div class="pg-trans-rec__subsection">
              <div class="p-form-grid p-form-grid--2">
                <div class="pg-trans-rec__text-col">
                  <div class="p-field">
                    <label class="p-field__label">
                      <i class="material-icons-round pg-trans-rec__field-ico">description</i>
                      Source Text
                    </label>
                    <textarea
                      v-model="exercise.sourceText"
                      class="p-input p-input--area pg-trans-rec__text-area"
                      rows="4"
                      placeholder="번역할 원문을 입력하세요..."
                      @input="updateWordCount(exercise)"
                    />
                  </div>
                  <div class="p-field">
                    <label class="p-field__label">
                      <i class="material-icons-round pg-trans-rec__field-ico">check_box</i>
                      Source Text 인정 답안
                    </label>
                    <textarea
                      v-model="exercise.sourceAcceptedAnswers"
                      class="p-input p-input--area"
                      rows="2"
                      placeholder="인정 답안을 쉼표로 구분하여 입력하세요 (예: 주치의, 외래 환자 주치의, 입원 환자 주치의)"
                    />
                  </div>
                </div>
                <div class="pg-trans-rec__text-col">
                  <div class="p-field">
                    <label class="p-field__label">
                      <i class="material-icons-round pg-trans-rec__field-ico">check_circle</i>
                      Reference Translation
                    </label>
                    <textarea
                      v-model="exercise.referenceTranslation"
                      class="p-input p-input--area pg-trans-rec__text-area"
                      rows="4"
                      placeholder="참고 번역문을 입력하세요..."
                    />
                  </div>
                  <div class="p-field">
                    <label class="p-field__label">
                      <i class="material-icons-round pg-trans-rec__field-ico">check_box</i>
                      Reference 인정 답안
                    </label>
                    <textarea
                      v-model="exercise.referenceAcceptedAnswers"
                      class="p-input p-input--area"
                      rows="2"
                      placeholder="인정 답안을 쉼표로 구분하여 입력하세요 (예: doctor, physician, primary care doctor, attending)"
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- Exercise Settings -->
            <div class="pg-trans-rec__subsection">
              <label class="pg-trans-rec__sub-label">
                <i class="material-icons-round pg-trans-rec__field-ico">tune</i>
                Exercise Settings
              </label>
              <div class="p-form-grid p-form-grid--4">
                <div class="p-field">
                  <label class="p-field__label">Difficulty</label>
                  <select v-model="exercise.difficulty" class="p-input">
                    <option value="beginner">🟢 초급 (Beginner)</option>
                    <option value="intermediate">🟡 중급 (Intermediate)</option>
                    <option value="advanced">🟠 고급 (Advanced)</option>
                    <option value="expert">🔴 전문가 (Expert)</option>
                  </select>
                </div>
                <div class="p-field">
                  <label class="p-field__label">Text Type</label>
                  <select v-model="exercise.textType" class="p-input">
                    <option value="general">📝 일반 (General)</option>
                    <option value="literary">📚 문학 (Literary)</option>
                    <option value="technical">⚙️ 기술 (Technical)</option>
                    <option value="legal">⚖️ 법률 (Legal)</option>
                    <option value="medical">🏥 의학 (Medical)</option>
                    <option value="business">💼 비즈니스 (Business)</option>
                  </select>
                </div>
                <div class="p-field">
                  <label class="p-field__label">Time Limit (min)</label>
                  <input
                    type="number"
                    v-model.number="exercise.timeLimit"
                    class="p-input"
                    min="5"
                    max="120"
                    placeholder="제한 시간"
                  />
                </div>
                <div class="p-field">
                  <label class="p-field__label">Word Count</label>
                  <div class="pg-trans-rec__word-count">
                    <span class="pg-trans-rec__word-badge">{{ exercise.wordCount || 0 }} words</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Instructions and Key Terms -->
            <div class="pg-trans-rec__subsection">
              <div class="p-form-grid p-form-grid--2">
                <div class="p-field">
                  <label class="p-field__label">
                    <i class="material-icons-round pg-trans-rec__field-ico">lightbulb</i>
                    Special Instructions
                  </label>
                  <textarea
                    v-model="exercise.instructions"
                    class="p-input p-input--area"
                    rows="2"
                    placeholder="특별 지침이나 주의사항을 입력하세요..."
                  />
                </div>
                <div class="p-field">
                  <label class="p-field__label">
                    <i class="material-icons-round pg-trans-rec__field-ico">vpn_key</i>
                    Key Terms/Phrases
                  </label>
                  <textarea
                    v-model="exercise.keyTerms"
                    class="p-input p-input--area"
                    rows="2"
                    placeholder="핵심 용어나 구문을 입력하세요 (한 줄에 하나씩)..."
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="pg-trans-rec__actions">
          <ABtn kind="yellow" icon="add" @click.prevent="addExercise">Add Translation Exercise</ABtn>
          <ABtn kind="ghost" icon="upload_file" @click.prevent="triggerExcelUpload">Import from Excel</ABtn>
          <input
            ref="excelFileInput"
            type="file"
            accept=".xlsx, .xls"
            class="pg-trans-rec__file"
            @change="handleExcelUpload"
          />
        </div>
      </div>
    </div>

    <!-- Evaluation Settings -->
    <div class="pg-trans-rec__section">
      <label class="pg-trans-rec__section-label">
        <i class="material-icons-round">star</i>
        Evaluation Settings
      </label>
      <div class="pg-trans-rec__evaluation">
        <div class="p-form-grid p-form-grid--3">
          <div class="p-field">
            <label class="p-field__label">Accuracy Weight (%)</label>
            <input
              type="number"
              v-model.number="sectionData.content.accuracyWeight"
              class="p-input"
              min="0"
              max="100"
              placeholder="정확성"
            />
          </div>
          <div class="p-field">
            <label class="p-field__label">Fluency Weight (%)</label>
            <input
              type="number"
              v-model.number="sectionData.content.fluencyWeight"
              class="p-input"
              min="0"
              max="100"
              placeholder="유창성"
            />
          </div>
          <div class="p-field">
            <label class="p-field__label">Style Weight (%)</label>
            <input
              type="number"
              v-model.number="sectionData.content.styleWeight"
              class="p-input"
              min="0"
              max="100"
              placeholder="문체"
            />
          </div>
        </div>
        <p class="pg-trans-rec__eval-hint">
          <i class="material-icons-round pg-trans-rec__hint-ico">info</i>
          평가 가중치의 합계는 100%가 되어야 합니다.
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineModel, watch, ref } from 'vue';
import * as XLSX from 'xlsx';
import ABtn from "@/components/Pineapple/ABtn.vue";

const sectionData = defineModel({ type: Object, required: true });
const excelFileInput = ref(null);

// 단어 수 계산 함수
const updateWordCount = (exercise) => {
  if (exercise.sourceText) {
    const words = exercise.sourceText.trim().split(/\s+/).filter(word => word.length > 0);
    exercise.wordCount = words.length;
  } else {
    exercise.wordCount = 0;
  }
};

// 데이터 구조 초기화
watch(() => sectionData.value.content, (newContent) => {
  if (!newContent.exercises) {
    sectionData.value.content.exercises = [];
  }
  if (!newContent.accuracyWeight) {
    sectionData.value.content.accuracyWeight = 40;
  }
  if (!newContent.fluencyWeight) {
    sectionData.value.content.fluencyWeight = 30;
  }
  if (!newContent.styleWeight) {
    sectionData.value.content.styleWeight = 30;
  }
}, { immediate: true, deep: true });

const addExercise = () => {
  sectionData.value.content.exercises.push({
    context: '',
    sourceLanguage: '',
    targetLanguage: '',
    sourceText: '',
    sourceAcceptedAnswers: '',
    referenceTranslation: '',
    referenceAcceptedAnswers: '',
    difficulty: 'intermediate',
    textType: 'general',
    timeLimit: 30,
    wordCount: 0,
    instructions: '',
    keyTerms: ''
  });
};

const removeExercise = (index) => {
  sectionData.value.content.exercises.splice(index, 1);
};

const triggerExcelUpload = () => {
  excelFileInput.value.click();
};

const handleExcelUpload = async (event) => {
  const file = event.target.files[0];
  if (!file) return;

  try {
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data);
    const sheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[sheetName];
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

    let addedCount = 0;

    // 각 행을 순회하며 데이터 추가
    // Excel 컬럼 순서: Context, Source Language, Target Language, Source Text, Source Accepted Answers,
    // Reference Translation, Reference Accepted Answers, Difficulty, Text Type, Time Limit, Instructions, Key Terms
    jsonData.forEach((row) => {
      // 빈 행은 건너뛰기
      if (!row || row.length === 0) return;

      const context = row[0] ? String(row[0]).trim() : '';
      const sourceLanguage = row[1] ? String(row[1]).trim() : '';
      const targetLanguage = row[2] ? String(row[2]).trim() : '';
      const sourceText = row[3] ? String(row[3]).trim() : '';
      const sourceAcceptedAnswers = row[4] ? String(row[4]).trim() : '';
      const referenceTranslation = row[5] ? String(row[5]).trim() : '';
      const referenceAcceptedAnswers = row[6] ? String(row[6]).trim() : '';
      const difficulty = row[7] ? String(row[7]).trim() : 'intermediate';
      const textType = row[8] ? String(row[8]).trim() : 'general';
      const timeLimit = row[9] ? (isNaN(Number(row[9])) ? 30 : Number(row[9])) : 30;
      const instructions = row[10] ? String(row[10]).trim() : '';
      const keyTerms = row[11] ? String(row[11]).trim() : '';

      // 최소한 sourceText나 referenceTranslation 중 하나에 데이터가 있으면 추가
      if (sourceText || referenceTranslation) {
        sectionData.value.content.exercises.push({
          context: context || '',
          sourceLanguage: sourceLanguage || '',
          targetLanguage: targetLanguage || '',
          sourceText: sourceText,
          sourceAcceptedAnswers: sourceAcceptedAnswers || '',
          referenceTranslation: referenceTranslation,
          referenceAcceptedAnswers: referenceAcceptedAnswers || '',
          difficulty: difficulty || 'intermediate',
          textType: textType || 'general',
          timeLimit: timeLimit,
          wordCount: sourceText ? sourceText.trim().split(/\s+/).filter(word => word.length > 0).length : 0,
          instructions: instructions || '',
          keyTerms: keyTerms || ''
        });
        addedCount++;
      }
    });

    alert(`${addedCount}개의 항목을 추가했습니다.`);

    // 파일 입력 초기화
    event.target.value = '';
  } catch (error) {
    console.error('Excel 파일 처리 오류:', error);
    alert('Excel 파일을 읽는 중 오류가 발생했습니다. 파일 형식을 확인해주세요.');
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.pg-trans-rec {
  display: flex;
  flex-direction: column;
  gap: 20px;

  &__header {
    padding-bottom: 12px;
    border-bottom: 1px solid $p-line;
  }

  &__title {
    font-size: 18px;
    font-weight: 700;
    color: $p-ink;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 10px;

    > i {
      font-size: 22px;
      color: $p-point-deep;
    }
  }

  &__section {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__section-label {
    font-size: 14px;
    font-weight: 700;
    color: $p-ink;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    padding-bottom: 8px;
    border-bottom: 1px dashed $p-line;

    > i {
      font-size: 18px;
      color: $p-ink-mid;
    }
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__item {
    background: $p-panel;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    padding: 14px 16px;
    transition: border-color $p-dur $p-ease;

    &:hover {
      border-color: $p-line-hard;
    }
  }

  &__item-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    padding-bottom: 10px;
    border-bottom: 1px dashed $p-line;
  }

  &__index {
    padding: 4px 12px;
    border-radius: 999px;
    background: $p-ink;
    color: $p-point;
    font-family: $p-font-mono;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.04em;
  }

  &__item-actions {
    display: flex;
    gap: 4px;
  }

  &__icon-btn {
    width: 30px;
    height: 30px;
    border: 0;
    background: transparent;
    color: $p-ink-mid;
    border-radius: $p-radius-sm;
    display: grid;
    place-items: center;
    cursor: pointer;
    transition: background $p-dur $p-ease, color $p-dur $p-ease;

    > i { font-size: 16px; }

    &:hover:not(:disabled) {
      background: $p-panel-alt;
      color: $p-ink;
    }

    &--danger {
      color: $p-err;

      &:hover:not(:disabled) {
        background: $p-err-soft;
        color: $p-err;
      }
    }
  }

  &__item-body {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__subsection {
    padding: 12px;
    background: $p-panel-alt;
    border: 1px solid $p-line-soft;
    border-radius: $p-radius-sm;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__sub-label {
    font-size: 13px;
    font-weight: 700;
    color: $p-ink;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  &__field-ico {
    font-size: 14px !important;
    color: $p-ink-mid;
    vertical-align: middle;
    margin-right: 4px;
  }

  &__text-col {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__text-area {
    min-height: 100px;
    line-height: 1.6;
  }

  &__word-count {
    display: flex;
    align-items: center;
    height: 40px;
  }

  &__word-badge {
    background: $p-ok;
    color: #fff;
    padding: 6px 12px;
    border-radius: 999px;
    font-weight: 700;
    font-size: 12px;
    font-family: $p-font-mono;
  }

  &__actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__file {
    display: none;
  }

  &__evaluation {
    background: $p-panel;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    padding: 14px 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__eval-hint {
    margin: 0;
    padding: 10px 12px;
    background: $p-panel-alt;
    border-radius: $p-radius-sm;
    border-left: 3px solid $p-info;
    font-size: 12px;
    color: $p-ink-mid;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  &__hint-ico {
    font-size: 14px !important;
    color: $p-info;
  }
}
</style>
